import React from 'react';
import { Box, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import NumberFormat from 'react-number-format';
import { useHistory } from 'react-router-dom';
import { StyledContainerRoot } from '../../Assignments/AssignmentsTable/List.styled';
import { DistrictSort, PaginationProps } from '../../../variables/types';
import CustomPagination from '../../../components/CustomPagination';
import NoResults from '../../../components/NoResults';
import WavingLadyFrame from '../../../components/Icons/WavingLadyFrame';
import { DistrictDetails, OrderBy } from '../../../generated/graphql';
import ArrowDownIcon from '../../../components/Icons/ArrowDownIcon';
import { StyledTableCell, StyledTableDataCell, StyledTableDataColumn } from '../../Admin/admin-tables-styled';
import { StyledTableSort, tableWithNoDataStyles } from '../../../components/Common/Common.styled';
import useLocalStorageState from '../../../hooks/useLocalStorageState';

interface Props {
  isTableEmpty: boolean;
  pagination: PaginationProps,
  loading: boolean,
  totalCount: number,
  sort: DistrictSort,
  order: OrderBy,
  districts: DistrictDetails[],
  handlePageChange: (_: React.ChangeEvent<unknown>,
    value: React.SetStateAction<number>) => void | undefined,
  handleSort: ((column: DistrictSort) => void) | Function,
  rowsPerPage: number,
  setRowsPerPage: Function,
}

const List = ({
  isTableEmpty,
  pagination,
  loading,
  totalCount,
  sort,
  order,
  handlePageChange,
  districts,
  handleSort,
  rowsPerPage,
  setRowsPerPage,
}: Props) => {
  const history = useHistory();
  const [currDistrictsPage, setCurrDistrictsPage] = useLocalStorageState('redirectDistrictsPage', history.location);
  const handleDistrictLinkClick = (districtId: string) => {
    setCurrDistrictsPage(currDistrictsPage ?? history.location);
    history.push(`/districts/${districtId}`);
  };
  const orderByIcon = order === OrderBy.Desc ? 'asc' : 'desc';
  const sortColumn = (title: string, sortEnum: DistrictSort) => (
    <StyledTableSort
      direction={orderByIcon}
      onClick={handleSort(sortEnum)}
      active={sort === sortEnum}
      IconComponent={ArrowDownIcon}
    >
      { title }
    </StyledTableSort>
  );
  return (
    <Box
      display="grid"
      width="100%"
      mt={2}
    >
      <Box>
        <StyledContainerRoot>
          <Table
            stickyHeader
            aria-label="districts table"
            sx={{ ...((loading || isTableEmpty) && tableWithNoDataStyles.noData) }}
          >
            <TableHead>
              <TableRow>
                <StyledTableCell sx={{ textAlign: 'left' }}>
                  { sortColumn('District', DistrictSort.Name) }
                </StyledTableCell>
                <StyledTableCell>
                  { sortColumn('District ID', DistrictSort.DistrictId) }
                </StyledTableCell>
                <StyledTableDataCell>
                  { sortColumn('State', DistrictSort.State) }
                </StyledTableDataCell>
                <StyledTableDataCell>
                  { sortColumn('Schools', DistrictSort.SchoolCount) }
                </StyledTableDataCell>
                <StyledTableDataCell>
                  { sortColumn('Students', DistrictSort.StudentCount) }
                </StyledTableDataCell>
                <StyledTableDataCell>
                  { sortColumn('Source', DistrictSort.Source) }
                </StyledTableDataCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && districts?.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    <Typography
                      variant='redirectionLinks'
                      onClick={() => handleDistrictLinkClick(row.id)}
                    >
                      {row.name}
                    </Typography>
                  </TableCell>
                  <StyledTableDataColumn>
                    {row?.district_id}
                  </StyledTableDataColumn>
                  <StyledTableDataColumn>
                    {row?.state?.name ?? '-'}
                  </StyledTableDataColumn>
                  <StyledTableDataColumn>
                    <NumberFormat
                      displayType="text"
                      value={row?.school_count ?? '-'}
                      thousandSeparator
                    />
                  </StyledTableDataColumn>
                  <StyledTableDataColumn>
                    <NumberFormat
                      displayType="text"
                      value={row?.student_count ?? '-'}
                      thousandSeparator
                    />
                  </StyledTableDataColumn>
                  <StyledTableDataColumn>
                    <Typography variant='capitalized'>
                      {row?.source}
                    </Typography>
                  </StyledTableDataColumn>
                </TableRow>
              ))}
              {isTableEmpty && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                  >
                    <NoResults
                      label="No Districts"
                      description="There has yet to be a district created."
                      svgComponent={<WavingLadyFrame />}
                    />
                  </TableCell>
                </TableRow>
              )}
              {loading && (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    align="center"
                  >
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledContainerRoot>
      </Box>  
      <CustomPagination
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        page={pagination.page}
        onChange={handlePageChange}
        setRowsPerPage={setRowsPerPage}
      />
    </Box>
  );
};
export default List;
